import React from 'react';
import { 
  Globe, 
  Smartphone, 
  Monitor, 
  Eye, 
  TrendingUp, 
  Target, 
  Activity 
} from 'lucide-react';

const TrafficAnalyticsTab = ({ trafficAnalytics }) => {
  return (
    <div className="space-y-6">
      {/* Traffic Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
          <p className="text-xs text-[#FDF2F8]/60 mb-1">Page Views Today</p>
          <p className="text-2xl font-bold text-white">{trafficAnalytics?.page_views?.today || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
          <p className="text-xs text-[#FDF2F8]/60 mb-1">Page Views This Week</p>
          <p className="text-2xl font-bold text-white">{trafficAnalytics?.page_views?.this_week || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
          <p className="text-xs text-[#FDF2F8]/60 mb-1">Total Page Views</p>
          <p className="text-2xl font-bold text-white">{trafficAnalytics?.page_views?.total || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
          <p className="text-xs text-[#FDF2F8]/60 mb-1">Avg Session Duration</p>
          <p className="text-2xl font-bold text-white">
            {trafficAnalytics?.sessions?.avg_duration 
              ? `${Math.round(trafficAnalytics.sessions.avg_duration / 60)}m` 
              : '0m'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Browsers */}
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
          <h3 className="text-sm font-semibold text-[#FDF2F8] mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#df1283]" /> Browsers
          </h3>
          <div className="space-y-2">
            {trafficAnalytics?.browsers?.slice(0, 5).map((b, i) => (
              <div key={b.browser || `browser-${i}`} className="flex justify-between items-center">
                <span className="text-sm text-[#FDF2F8]/80">{b.browser}</span>
                <span className="text-sm font-medium text-white">{b.count}</span>
              </div>
            ))}
            {(!trafficAnalytics?.browsers || trafficAnalytics.browsers.length === 0) && (
              <p className="text-sm text-[#FDF2F8]/50">No browser data yet - data will appear as users visit</p>
            )}
          </div>
        </div>

        {/* Devices */}
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
          <h3 className="text-sm font-semibold text-[#FDF2F8] mb-4 flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-[#FF7901]" /> Devices
          </h3>
          <div className="space-y-2">
            {trafficAnalytics?.devices?.map((d, i) => (
              <div key={d.device || `device-${i}`} className="flex justify-between items-center">
                <span className="text-sm text-[#FDF2F8]/80">{d.device}</span>
                <span className="text-sm font-medium text-white">{d.count}</span>
              </div>
            ))}
            {(!trafficAnalytics?.devices || trafficAnalytics.devices.length === 0) && (
              <p className="text-sm text-[#FDF2F8]/50">No device data yet</p>
            )}
          </div>
        </div>

        {/* Operating Systems */}
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
          <h3 className="text-sm font-semibold text-[#FDF2F8] mb-4 flex items-center gap-2">
            <Monitor className="w-4 h-4 text-[#22C55E]" /> Operating Systems
          </h3>
          <div className="space-y-2">
            {trafficAnalytics?.operating_systems?.slice(0, 5).map((os, i) => (
              <div key={os.os || `os-${i}`} className="flex justify-between items-center">
                <span className="text-sm text-[#FDF2F8]/80">{os.os}</span>
                <span className="text-sm font-medium text-white">{os.count}</span>
              </div>
            ))}
            {(!trafficAnalytics?.operating_systems || trafficAnalytics.operating_systems.length === 0) && (
              <p className="text-sm text-[#FDF2F8]/50">No OS data yet</p>
            )}
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
          <h3 className="text-sm font-semibold text-[#FDF2F8] mb-4 flex items-center gap-2">
            <Eye className="w-4 h-4 text-[#8B5CF6]" /> Top Pages
          </h3>
          <div className="space-y-2">
            {trafficAnalytics?.top_pages?.slice(0, 5).map((p, i) => (
              <div key={p.page || `page-${i}`} className="flex justify-between items-center">
                <span className="text-sm text-[#FDF2F8]/80 truncate max-w-[150px]">{p.page}</span>
                <span className="text-sm font-medium text-white">{p.views}</span>
              </div>
            ))}
            {(!trafficAnalytics?.top_pages || trafficAnalytics.top_pages.length === 0) && (
              <p className="text-sm text-[#FDF2F8]/50">No page data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* UTM Tracking */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* UTM Sources */}
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
          <h3 className="text-sm font-semibold text-[#FDF2F8] mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#df1283]" /> UTM Sources
          </h3>
          <p className="text-xs text-[#FDF2F8]/40 mb-3">Track with: ?utm_source=instagram</p>
          <div className="space-y-2">
            {trafficAnalytics?.utm_sources?.map((u, i) => (
              <div key={u.source || `utm-${i}`} className="flex justify-between items-center">
                <span className="text-sm text-[#FDF2F8]/80">{u.source}</span>
                <span className="text-sm font-medium text-white">{u.count}</span>
              </div>
            ))}
            {(!trafficAnalytics?.utm_sources || trafficAnalytics.utm_sources.length === 0) && (
              <p className="text-sm text-[#FDF2F8]/50">No UTM sources tracked yet</p>
            )}
          </div>
        </div>

        {/* UTM Campaigns */}
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
          <h3 className="text-sm font-semibold text-[#FDF2F8] mb-4 flex items-center gap-2">
            <Target className="w-4 h-4 text-[#FF7901]" /> UTM Campaigns
          </h3>
          <p className="text-xs text-[#FDF2F8]/40 mb-3">Track with: ?utm_campaign=spring_promo</p>
          <div className="space-y-2">
            {trafficAnalytics?.utm_campaigns?.map((c, i) => (
              <div key={c.campaign || `campaign-${i}`} className="flex justify-between items-center">
                <span className="text-sm text-[#FDF2F8]/80">{c.campaign}</span>
                <span className="text-sm font-medium text-white">{c.count}</span>
              </div>
            ))}
            {(!trafficAnalytics?.utm_campaigns || trafficAnalytics.utm_campaigns.length === 0) && (
              <p className="text-sm text-[#FDF2F8]/50">No campaigns tracked yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent IP Addresses */}
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
        <h3 className="text-sm font-semibold text-[#FDF2F8] mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#df1283]" /> Recent IP Addresses
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#FDF2F8]/60 border-b border-[#4A0A66]">
                <th className="text-left py-2 px-2">IP Address</th>
                <th className="text-left py-2 px-2">Page Views</th>
                <th className="text-left py-2 px-2">Last Seen</th>
                <th className="text-left py-2 px-2">User</th>
              </tr>
            </thead>
            <tbody>
              {trafficAnalytics?.recent_ips?.slice(0, 10).map((ip, i) => (
                <tr key={ip._id || `ip-${i}`} className="border-b border-[#4A0A66]/30">
                  <td className="py-2 px-2 text-white font-mono text-xs">{ip._id}</td>
                  <td className="py-2 px-2 text-[#FDF2F8]/80">{ip.page_views}</td>
                  <td className="py-2 px-2 text-[#FDF2F8]/60 text-xs">
                    {ip.last_seen ? new Date(ip.last_seen).toLocaleString() : '-'}
                  </td>
                  <td className="py-2 px-2 text-[#FDF2F8]/60 text-xs">
                    {ip.user_id || 'Anonymous'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!trafficAnalytics?.recent_ips || trafficAnalytics.recent_ips.length === 0) && (
            <p className="text-sm text-[#FDF2F8]/50 py-4 text-center">No IP data yet - IPs will be logged as users visit</p>
          )}
        </div>
      </div>

      {/* Events Tracking */}
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
        <h3 className="text-sm font-semibold text-[#FDF2F8] mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#22C55E]" /> Event Tracking
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {trafficAnalytics?.events?.map((e, i) => (
            <div key={i} className="bg-[#4A0A66]/30 rounded-lg p-3 text-center">
              <p className="text-xs text-[#FDF2F8]/60 truncate">{e.event}</p>
              <p className="text-lg font-bold text-white">{e.count}</p>
            </div>
          ))}
          {(!trafficAnalytics?.events || trafficAnalytics.events.length === 0) && (
            <p className="text-sm text-[#FDF2F8]/50 col-span-4">Events will be tracked as users interact with the app</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrafficAnalyticsTab;
